import type {
  AmountSource,
  CoinsuranceBasis,
  CopayPresentation,
  DeductibleHandling,
  OutOfNetworkPolicy,
  PriceQuoteKind,
  PricingRule,
  PricingRuleConditions,
} from '@platform/contracts';

/**
 * The pricing editor's vocabulary (MASTER_PLAN M6.11, FR-INS-07, FR-ADMIN-06).
 *
 * Every enum the ruleset uses is spelled out here once, as plain English, because the
 * screen's whole job is to make a rule engine legible to a clinic manager. `copay`,
 * `coinsurance` and `contracted` are insurance words; "what the payer said the patient
 * pays per visit" is the sentence beside it that makes the choice answerable.
 *
 * The `Record<Enum, …>` shape is deliberate: adding a value to a contract enum makes
 * this file a compile error, which is how a new outcome gets a label before it reaches a
 * screen instead of after.
 */

/**
 * The keys the pricing screens read, spelled exactly as `SECURITY.md` §5.1 and
 * `Docs/API.md` §2.6 spell them.
 *
 * They are named here rather than in `lib/session/permissions.ts` for the same reason
 * `FEEDBACK_PERMISSIONS` is: the feature owns its keys, and a mistyped key fails *closed*
 * — the item silently disappears for everyone and nothing points at why.
 */
export const PRICING_PERMISSIONS = {
  /** Opens `/pricing`. */
  read: 'pricing.read',
  /** Publishes a new version. Also `@BlockedWhileImpersonating` on the server. */
  manage: 'pricing.manage',
  /** Lists billing NPIs. */
  insuranceRead: 'insurance.read',
  /** Reads the AI copay-estimate settings. */
  aiRead: 'ai_estimates.read',
  /** Registers an NPI, records the attestation, changes the AI settings, refreshes payers. */
  settingsManage: 'tenant.settings.manage',
} as const;

/* ------------------------------------------------------------------ labels ---- */

export const COPAY_PRESENTATION_LABELS: Record<CopayPresentation, string> = {
  estimate: 'Show the estimated amount',
  range: 'Show a range around the estimate',
  card_on_file: 'Take a card and confirm the cost later',
};

export const COPAY_PRESENTATION_HELP: Record<CopayPresentation, string> = {
  estimate:
    'The patient sees one number, labelled as an estimate. Clearest to read, and the one most likely to be wrong by a few dollars.',
  range:
    'The patient sees a low and a high figure either side of the estimate, using the spread below. Sets expectations without committing to a number.',
  card_on_file:
    'The patient sees no amount at booking. You take a card, confirm the cost, and charge it afterwards.',
};

export const DEDUCTIBLE_HANDLING_LABELS: Record<DeductibleHandling, string> = {
  ignore: 'Ignore it and quote the copay',
  add_to_copay: 'Add the remaining deductible to the copay',
  review: 'Send it to staff review',
};

export const DEDUCTIBLE_HANDLING_HELP: Record<DeductibleHandling, string> = {
  ignore: 'Quotes the copay as if the deductible were already met. Optimistic; under-charges.',
  add_to_copay: 'Quotes copay plus what is left of the deductible, capped at the self-pay price.',
  review:
    'The safe default. A patient with $1,400 of deductible left usually owes the visit price, not the copay, and no payer answer says reliably which applies.',
};

export const COINSURANCE_BASIS_LABELS: Record<CoinsuranceBasis, string> = {
  contracted: 'The contracted (allowed) amount',
  self_pay: 'The self-pay price',
};

export const OUT_OF_NETWORK_POLICY_LABELS: Record<OutOfNetworkPolicy, string> = {
  self_pay: 'Quote the self-pay price',
  review: 'Send it to staff review',
};

export const QUOTE_KIND_LABELS: Record<PriceQuoteKind, string> = {
  self_pay: 'Self-pay',
  copay_estimate: 'Copay estimate',
  pending_review: 'Waiting for staff',
};

export const AMOUNT_SOURCE_LABELS: Record<AmountSource, string> = {
  none: 'No amount — the quote waits for staff',
  self_pay: 'The self-pay price for the visit type',
  contracted: 'The contracted (allowed) amount',
  copay: 'The copay the payer reported',
  copay_plus_deductible: 'The copay plus what is left of the deductible',
  coinsurance: 'The coinsurance percentage, applied to the basis above',
  fixed: 'A fixed amount you type below',
};

/* ------------------------------------------------------------- conditions ---- */

/** One row of the conditions builder: a documented condition field and its choices. */
export interface ConditionField {
  key: keyof PricingRuleConditions;
  label: string;
  help: string;
  /** `null` for the free-text appointment type key, which is a select over live types. */
  choices: readonly { value: string; label: string }[] | null;
  /** `true` when the stored value is a boolean rather than a string. */
  boolean?: boolean;
}

/**
 * Every field `PricingRuleConditionsSchema` declares, in the order a person thinks about
 * them: what is being booked, then what the payer said, then how the check itself went.
 * An absent field means "do not care", which is what the "Any" choice sends.
 */
export const CONDITION_FIELDS: readonly ConditionField[] = Object.freeze([
  {
    key: 'appointmentTypeKey',
    label: 'Visit type',
    help: 'Matches one appointment type by its key.',
    choices: null,
  },
  {
    key: 'coverage',
    label: 'Coverage',
    help: 'What the payer said about the policy. "None" is a patient who chose self-pay.',
    choices: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Not active' },
      { value: 'unknown', label: 'The payer did not say' },
      { value: 'none', label: 'The patient chose self-pay' },
    ],
  },
  {
    key: 'network',
    label: 'Network',
    help: 'Whether this clinic is in the patient’s network for this plan.',
    choices: [
      { value: 'in_network', label: 'In network' },
      { value: 'out_of_network', label: 'Out of network' },
      { value: 'unknown', label: 'Not stated' },
    ],
  },
  {
    key: 'hasCopay',
    label: 'Copay reported',
    help: 'The payer gave a per-visit copay amount.',
    boolean: true,
    choices: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    key: 'hasCoinsurance',
    label: 'Coinsurance reported',
    help: 'The payer gave a percentage rather than an amount.',
    boolean: true,
    choices: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    key: 'deductibleRemaining',
    label: 'Deductible left',
    help: 'Whether the patient still has deductible to meet this year.',
    choices: [
      { value: 'none', label: 'None left' },
      { value: 'positive', label: 'Some left' },
    ],
  },
  {
    key: 'payerCategory',
    label: 'Payer type',
    help: 'Medicare checks additionally need an attested billing NPI.',
    choices: [
      { value: 'commercial', label: 'Commercial' },
      { value: 'medicare', label: 'Medicare' },
      { value: 'medicaid', label: 'Medicaid' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    key: 'eligibility',
    label: 'How the check went',
    help: 'The outcome of the check itself, whatever it found.',
    choices: [
      { value: 'verified', label: 'We got an answer' },
      { value: 'failed', label: 'The details were rejected' },
      { value: 'unavailable', label: 'The payer did not answer' },
      { value: 'skipped', label: 'No check was run' },
    ],
  },
]);

/** How many conditions a rule carries. Zero means it is the catch-all. */
export function conditionCount(rule: PricingRule): number {
  return Object.values(rule.conditions).filter((value) => value !== undefined).length;
}
